import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/lib/supabase";
import type { DashboardStats } from "@/types";
import { Package, ShoppingCart, Store, TrendingUp } from "lucide-react";
import { useEffect, useState } from "react";

function StatCard({
	title,
	value,
	subtitle,
	icon: Icon,
}: {
	title: string;
	value: string | number;
	subtitle?: string;
	icon: React.ElementType;
}) {
	return (
		<Card>
			<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
				<CardTitle className="text-sm font-medium">{title}</CardTitle>
				<Icon className="h-4 w-4 text-muted-foreground" />
			</CardHeader>
			<CardContent>
				<div className="text-2xl font-bold">{value}</div>
				{subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
			</CardContent>
		</Card>
	);
}

export function DashboardPage() {
	const [stats, setStats] = useState<DashboardStats | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		async function fetchStats() {
			// Fetch counts from Supabase directly
			const [storesResult, ordersResult, ordersWeekResult] = await Promise.all([
				supabase.from("stores").select("id, status, products_synced_count"),
				supabase.from("orders").select("id, status, created_at"),
				supabase
					.from("orders")
					.select("id, status")
					.gte("created_at", new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()),
			]);

			const stores = storesResult.data ?? [];
			const orders = ordersResult.data ?? [];
			const ordersWeek = ordersWeekResult.data ?? [];

			const today = new Date().toISOString().split("T")[0];
			const ordersToday = orders.filter((o) => o.created_at?.startsWith(today));

			const placed = orders.filter((o) => o.status === "placed").length;
			const draft = orders.filter((o) => o.status === "draft").length;
			const failed = orders.filter((o) => o.status === "failed").length;
			const totalProcessed = placed + draft + failed;

			setStats({
				total_stores: stores.length,
				active_stores: stores.filter((s) => s.status === "active").length,
				total_products_synced: stores.reduce((sum, s) => sum + (s.products_synced_count ?? 0), 0),
				total_orders: orders.length,
				orders_today: ordersToday.length,
				orders_this_week: ordersWeek.length,
				orders_placed: placed,
				orders_draft: draft,
				orders_failed: failed,
				success_rate: totalProcessed > 0 ? Math.round((placed / totalProcessed) * 100) : 0,
			});
			setLoading(false);
		}

		fetchStats();
	}, []);

	if (loading) {
		return (
			<div className="flex items-center justify-center py-20 text-muted-foreground">
				Loading dashboard...
			</div>
		);
	}

	if (!stats) return null;

	return (
		<div className="space-y-8">
			<div>
				<h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
				<p className="text-muted-foreground">Overview of your email-to-order conversion pipeline</p>
			</div>

			<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
				<StatCard
					title="Active Stores"
					value={stats.active_stores}
					subtitle={`${stats.total_stores} total`}
					icon={Store}
				/>
				<StatCard
					title="Products Synced"
					value={stats.total_products_synced}
					subtitle="Across all stores"
					icon={Package}
				/>
				<StatCard
					title="Orders Today"
					value={stats.orders_today}
					subtitle={`${stats.orders_this_week} this week`}
					icon={ShoppingCart}
				/>
				<StatCard
					title="Success Rate"
					value={`${stats.success_rate}%`}
					subtitle={`${stats.orders_placed} placed / ${stats.orders_draft} drafts / ${stats.orders_failed} failed`}
					icon={TrendingUp}
				/>
			</div>

			<div className="grid gap-4 md:grid-cols-3">
				<Card>
					<CardHeader>
						<CardTitle className="text-base">Orders Placed</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="text-3xl font-bold text-green-600">{stats.orders_placed}</div>
						<p className="text-sm text-muted-foreground">Successfully submitted to Shopify</p>
					</CardContent>
				</Card>
				<Card>
					<CardHeader>
						<CardTitle className="text-base">Draft Orders</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="text-3xl font-bold text-yellow-600">{stats.orders_draft}</div>
						<p className="text-sm text-muted-foreground">Awaiting customer completion</p>
					</CardContent>
				</Card>
				<Card>
					<CardHeader>
						<CardTitle className="text-base">Failed Orders</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="text-3xl font-bold text-red-600">{stats.orders_failed}</div>
						<p className="text-sm text-muted-foreground">Require manual review</p>
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
